import { DragDropContext } from '@hello-pangea/dnd';
import { Box, List, useTheme } from '@mui/material';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKanbanContext } from '@/context/kanbanContext';
import {
	ticketsApi,
	useRetrieveFirebaseClaimStatusQuery,
	useUpdateInsightClaimMutation,
} from '@/features/claims/ticketsApi';
import { dealsApi, useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import {
	clientsApi,
	useUpdateClientRowMutation,
	useUpdateClientxMutation,
} from '@/features/clients/clientsApi';
import { IS_DEV } from '@/utils/environment';
import { KanbanColumn } from '../KanbanColumn';
import { engagementsApi } from '@/features/engagements/engagementsApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { showToast } from '@/features/toast/toastSlice';
import { openDialog } from '@/features/dialog/dialogSlice';
import { usePipelineContext } from '@/context/pipelineContext';
import { getComparator, stableSort } from '@/components/table/functions';
import { useColumnControl } from '@/context/columnControlContext.jsx';

const MIN_WIDTH = 250;
const MAX_WIDTH = 400;

export const KanbanContent = () => {
	const theme = useTheme();
	const dispatch = useDispatch();

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const containerRef = useRef();
	const [width, setWidth] = useState(MIN_WIDTH);

	const {
		pipelineId,
		isClaims,
		isEndorsement,
		isProspect,
		dealStages,
		shiftDeal,
		updateRefreshStatus,
	} = useKanbanContext();

	const isDebtor = pipelineId == process.env.REACT_APP_PIPELINE_DEBTORS;

	const { stages: contextStages } = usePipelineContext(pipelineId);

	const { hiddenColumns } = useColumnControl();

	const visibleStages = useMemo(
		() => contextStages?.filter((stage) => !hiddenColumns.includes(stage.id)) ?? [],
		[contextStages, hiddenColumns],
	);

	const stageData = useMemo(() => visibleStages ?? contextStages, [visibleStages, contextStages]);

	const stages = useMemo(() => {
		const list = stageData ?? [];
		return stableSort(list, getComparator('asc', 'displayOrder'));
	}, [stageData]);

	const isDeal = !(isClaims || (IS_DEV && isEndorsement));

	const { data: claimStatuses } = useRetrieveFirebaseClaimStatusQuery(
		{ ledger: userDetails?.broker?.ledger.split('__')[0].trim() },
		{ skip: !userDetails?.broker?.ledger || !isClaims },
	);

	const [updateDealRow] = useUpdateDealRowMutation();
	const [updateInsightClaim] = useUpdateInsightClaimMutation();
	const [updateClientRow] = useUpdateClientRowMutation();
	const [updateClientx] = useUpdateClientxMutation();

	const updateStage = async ({ currentStageId, newStageId, deal }) => {
		const currentStage = contextStages.find((s) => s.id === currentStageId);
		const newStage = contextStages.find((s) => s.id === newStageId);

		const isClosingDeal = newStage.completed || newStage.closed;
		const isReopeningDeal = currentStage.completed || currentStage.closed;

		const properties = {
			dealStage: newStage.id,
			...(isClosingDeal && {
				...(newStage.completed && {
					completed: true,
					completedBy: userDetails.userId,
				}),
				closed: true,
				dateClosed: new Date().getTime(),
				closedBy: userDetails.userId,
			}),
			...((isReopeningDeal || !isClosingDeal) && {
				completed: false,
				closed: false,
				dateClosed: null,
				completedBy: null,
				closedBy: null,
			}),
		};

		await updateDealRow({
			dealId: deal.dealId,
			docId: deal.docId,
			properties,
		}).unwrap();

		if (isClaims) {
			const closedStatus = claimStatuses.find((stage) => stage.isClosedStage);
			const openStatus = claimStatuses.find((stage) => stage.isFirstStage);

			if (isClosingDeal && closedStatus) {
				await updateInsightClaim({
					dealId: deal.id,
					properties: {
						StatusId: closedStatus?.insightStageId,
					},
				}).unwrap();
			} else if (isReopeningDeal && !isClosingDeal && openStatus) {
				await updateInsightClaim({
					dealId: deal.id,
					properties: {
						StatusId: openStatus?.insightStageId,
					},
				}).unwrap();
			}
		}
	};

	const updateProspectStage = async ({ newStageId, deal: prospect }) => {
		const newStage = contextStages.find((s) => s.id === newStageId || s.stageId === newStageId);
		if (!newStage) {
			throw new Error('Prospect stage not found');
		}

		const clientId = prospect.clientId || prospect.id;

		if (prospect.insightId != null) {
			const isClosed = Boolean(newStage?.closed);
			await updateClientx({
				clientId,
				insight: {
					CurrentStatusId: isClosed ? 1 : 0,
				},
				firebase: { stage: newStageId },
			}).unwrap();
		} else {
			await updateClientRow({
				clientId,
				docId: prospect.docId,
				properties: { stage: newStageId },
			}).unwrap();
		}
	};

	const invalidateCacheInBackground = () => {
		setTimeout(() => {
			try {
				if (isProspect) {
					dispatch(clientsApi.util.invalidateTags([{ type: 'CLIENTS', id: 'LIST' }]));
				} else {
					const type = isDeal ? 'DEAL' : 'TICKET';
					dispatch(
						(isDeal ? dealsApi : ticketsApi).util.invalidateTags([
							{ type: `${type}S`, id: 'LIST' },
						]),
					);
				}
				dispatch(engagementsApi.util.invalidateTags([{ type: 'TASKS', id: 'LIST' }]));
			} catch (error) {
				console.error('Error invalidating APIs:', error);
			}
		}, 1000);
	};

	const onDragEnd = async (result) => {
		const { destination, source, draggableId } = result;

		if (
			!destination ||
			(destination.droppableId === source.droppableId && destination.index === source.index)
		) {
			return;
		}

		const draggedItem = dealStages[source.droppableId]?.find((d) => d.id == draggableId);
		if (!draggedItem) return;

		const originalStage = source.droppableId;
		const originalIndex = source.index;
		const newStage = contextStages.find(
			(s) => s.id === destination.droppableId || s.stageId === destination.droppableId,
		);

		// Dropping onto a completed stage opens Complete Deal instead of auto-closing.
		if (!isProspect && newStage?.completed && destination.droppableId !== originalStage) {
			const dealId = String(draggedItem.dealId);
			if (!dealId) {
				console.error('[KanbanContent] Missing dealId for complete modal', draggedItem);
				return;
			}
			dispatch(
				openDialog({
					dialogName: 'completeDeal',
					data: { dealId },
				}),
			);
			return;
		}

		// Dropping onto a closed stage prompts for a loss reason first.
		if (
			newStage?.closed &&
			!isClaims &&
			!isDebtor &&
			destination.droppableId !== originalStage
		) {
			const lossReasonData = isProspect
				? {
						clientId: draggedItem.clientId,
						docId: draggedItem.docId,
						stageId: destination.droppableId,
						insightId: draggedItem.insightId,
					}
				: {
						dealId: draggedItem.dealId,
						docId: draggedItem.docId,
						stageId: destination.droppableId,
					};

			if (!isProspect && !lossReasonData.dealId) {
				console.error('[KanbanContent] Missing dealId for loss reason dialog', draggedItem);
				return;
			}

			dispatch(
				openDialog({
					dialogName: 'lossReason',
					data: lossReasonData,
				}),
			);
			return;
		}

		shiftDeal(
			source.droppableId,
			destination.droppableId,
			originalIndex,
			destination.index,
			draggedItem,
		);

		// Same-column reorder only — no stage API update needed
		if (destination.droppableId === source.droppableId) {
			return;
		}

		try {
			if (isProspect) {
				await updateProspectStage({
					newStageId: destination.droppableId,
					deal: draggedItem,
				});
			} else {
				await updateStage({
					currentStageId: originalStage,
					newStageId: destination.droppableId,
					deal: draggedItem,
				});
			}

			invalidateCacheInBackground();
		} catch (error) {
			console.error('Error updating stage:', error);
			updateRefreshStatus(originalStage, true);
			updateRefreshStatus(destination.droppableId, true);
			dispatch(
				showToast({
					message: isProspect
						? 'Failed to move prospect. Please try again.'
						: 'Failed to move deal. Please try again.',
					error: true,
					duration: 5000,
				}),
			);
		}
		setTimeout(() => {
			updateRefreshStatus(originalStage, true);
			updateRefreshStatus(destination.droppableId, true);
		}, 10000);
	};

	useEffect(() => console.log('stages', contextStages), [contextStages]);

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const updateWidth = () => {
			const stageCount = contextStages?.length ?? 0;
			if (stageCount === 0) {
				setWidth(MAX_WIDTH);
				return;
			}
			const available = container.offsetWidth;
			const nextWidth = Math.min(
				MAX_WIDTH,
				Math.max(MIN_WIDTH, Math.floor(available / stageCount)),
			);
			setWidth(nextWidth);
		};

		updateWidth();

		const observer = new ResizeObserver(updateWidth);
		observer.observe(container);

		return () => observer.disconnect();
	}, [contextStages?.length]);

	return (
		<Box px={3}>
			<List
				id='kanban-content-list'
				ref={containerRef}
				component='div'
				sx={{
					overflowY: 'auto',
					height: 'calc(100vh - 180px)',
					maxHeight: 'calc(100vh - 180px)',
				}}
			>
				<DragDropContext onDragEnd={onDragEnd}>
					<Box
						id='kanban-content-box'
						sx={{
							display: 'flex',
							width: 'max-content',
							// width: 'stretch',
							height: '100%',
							// A darker canvas than the cards' own `background.paper` surface, so
							// columns/cards stay visually distinct in dark mode (mirrors the light
							// mode relationship between this '#f5f8fa' canvas and white cards).
							bgcolor:
								theme.palette.mode === 'dark'
									? theme.palette.darkModeBackground
									: '#f5f8fa',
						}}
					>
						{stages?.map((stage) => (
							<KanbanColumn stageId={stage.id} key={stage.id} width={`${width}px`} />
						))}
					</Box>
				</DragDropContext>
			</List>
		</Box>
	);
};
